import {Observable} from 'rxjs';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {Filter, SimpleMultiSeries, SimpleUnit, ThresholdStatic} from '@core/interfaces/system/system-common';
import {ApmResultType, LatestModelStatus, LiveModelRequest} from './live';
import {DownloadCSVUrl} from '@core/interfaces/engin/workflow-validation';

export interface BatteryBankUnits {
    conductanceUnit: SimpleUnit;
    voltageUnit: SimpleUnit;
    resistanceUnit: SimpleUnit;
}

export enum BatteryBankLayout {
    SNAKE_COLUMN = 'SNAKE_COLUMN',
    SNAKE_ROW = 'SNAKE_ROW',
    RIGHT_TO_LEFT = 'RIGHT_TO_LEFT',
    LEFT_TO_RIGHT = 'LEFT_TO_RIGHT',
    TOP_TO_BOTTOM = 'TOP_TO_BOTTOM',
    BOTTOM_TO_TOP = 'BOTTOM_TO_TOP',
}

export interface BatteryBankCellStatusDto {
    NUMBER_OF_CELLS_TOTAL: string;
    batteryBankLayout: BatteryBankLayout;
    totalNumberOfCells: number;
    cellData: BatteryBankCellData[];
}

export interface BatteryBankCellData {
    VOLTAGE: string;
    STRAP_RESISTANCE: string;
    CONDUCTANCE: string;

    cellId: string;
    cellOrder: string;
    cellStatus: CellStatus;
    readingTime: Date;
    metrics: CellMetric[];
    isMissingData: boolean;
}

export interface CellInfoDto {
    cellOrder: number;
    cellId: string;
    cellStatus: CellStatus;
}

export enum CellStatus {
    NONE = 'NONE',
    GOOD = 'GOOD',
    WARNING = 'WARNING',
    FAULT = 'FAULT',
}

export interface CellMetric {
    label: string;
    value: string;
    order: number;
}

export interface BatteryBankMetricSummary {
    conductanceLatest: MetricSummaryDto;
    cellVoltageLatest: MetricSummaryDto;
}

export interface MetricSummaryDto {
    rawAverage: number;
    high: number;
    low: number;
    adjustedAverage: number;
}

export interface BatteryBankModelMetricRequest extends LiveModelRequest {
    chartType: BatteryBankChartTypes;
}

export interface BatteryBankCellMetricRequest extends BatteryBankModelMetricRequest {
    cellId: string;
}

export enum BatteryBankChartTypes {
    // Applies to battery banks and cells
    CELL_VOLTAGE = 'CELL_VOLTAGE',
    CELL_CONDUCTANCE = 'CELL_CONDUCTANCE',
    // Cells only
    STRAP_RESISTANCE = 'STRAP_RESISTANCE',
    RELATIVE_CONDUCTANCE = 'RELATIVE_CONDUCTANCE',
}

export interface BatteryBankMetricChartDto {
    data: SimpleMultiSeries<Date>;
    forecastStartDate: string | null;
}

export interface CellMetricTimeseriesChart {
    data: SimpleMultiSeries<string>;
    forecastStartDate: string | null;
    thresholds: ThresholdStatic[];
    replacementDates: string[];
}

export interface BatteryBankAnalysisDto {
    cellOrder: number;
    resultType: ApmResultType;
    readingTime: Date;
    cellConductance: number;
    strapResistance: number;
    cellVoltage: number;
    percAvConductance: number;
    percOriginalConductance: number;
    avgCellVoltage: number;
    avgCellConductance: number;
}

export interface CellMetricAnalysisDto {
    readingTime: Date;
    cellVoltage: number;
    strapResistance: number;
    cellConductance: number;
    avgCellVoltage: number;
    avgCellConductance: number;
    percAvConductance: number;
    percOriginalConductance: number;
    resultType: ApmResultType;
}

export interface CellMetricAggregatedChart {
    data: SimpleMultiSeries<string>;
    thresholds: ThresholdStatic[];
}

export abstract class BatteryBankService {
    // Generic
    abstract getUnits(): Observable<BatteryBankUnits>;
    abstract getLatestStatusDetails(request: LiveModelRequest): Observable<LatestModelStatus>;
    abstract getDatasource(id: string, filter: Filter[]): Observable<DataSource>;
    abstract getCountResults(id: string, uploadTime?: string): Observable<number>;

    // Charts > Battery banks
    abstract getBatteryBankMetricsLatest(request: LiveModelRequest): Observable<BatteryBankMetricSummary>;
    abstract getBatteryBankMetricByTime(request: BatteryBankModelMetricRequest): Observable<BatteryBankMetricChartDto>;

    // Charts > Cells
    abstract getCellsLatestStatus(request: LiveModelRequest): Observable<BatteryBankCellStatusDto>;
    abstract getCellsLatestStatusCsv(request: LiveModelRequest): Observable<DownloadCSVUrl>;
    abstract getCellsAggregatedChart(
        request: LiveModelRequest,
        readingMetricType: BatteryBankChartTypes,
    ): Observable<CellMetricAggregatedChart>;
    abstract getCellMetricChartByTime(request: BatteryBankCellMetricRequest): Observable<CellMetricTimeseriesChart>;

    // Cells
    abstract getCellOptions(request: LiveModelRequest): Observable<CellInfoDto[]>;
    abstract getCellResultList(request: LiveModelRequest, filters?: Filter[]): Observable<DataSource>; //BatteryBankAnalysisDto[]
    abstract getCellResultListCsv(
        request: LiveModelRequest,
        paramsString: string,
        filters?: Filter[],
    ): Observable<DownloadCSVUrl>;
}
